import React, { useState } from 'react';
import { Card, CardContent } from '../components/Card';
import { Button } from '../components/Button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/Table';
import { Badge } from '../components/Badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/Tabs';
import { FileText, Link as LinkIcon, CheckCircle2, Edit, Trash2, BookOpen, Shield } from 'lucide-react';
const codingDocs = [{
  id: '1',
  name: 'ICD-10-CM Official Guidelines for Coding and Reporting',
  type: 'Standard',
  status: 'Ready',
  updated: '15/02/2026',
  by: 'System'
}, {
  id: '2',
  name: 'OPCS-4 Reference Manual (NHS)',
  type: 'Standard',
  status: 'Ready',
  updated: '10/01/2026',
  by: 'System'
}, {
  id: '3',
  name: 'WHO International Classification of Diseases',
  type: 'URL',
  status: 'Ready',
  updated: '05/03/2026',
  by: 'Clinical Coder'
}, {
  id: '4',
  name: 'Sravani Internal Coding Policies Q1 2026',
  type: 'File',
  status: 'Ready',
  updated: '01/03/2026',
  by: 'Lead Coder'
}, {
  id: '5',
  name: 'Cardiology Specific Coding Updates',
  type: 'File',
  status: 'Processing',
  updated: '12/03/2026',
  by: 'Clinical Coder'
}];
const insuranceDocs = [{
  id: '1',
  name: 'Bupa Pre-authorisation Rules 2026',
  type: 'File',
  status: 'Ready',
  updated: '20/02/2026',
  by: 'Billing Admin'
}, {
  id: '2',
  name: 'AXA Health Dispute Resolution Guidelines',
  type: 'URL',
  status: 'Ready',
  updated: '18/02/2026',
  by: 'Billing Admin'
}, {
  id: '3',
  name: 'Aviva Claims Submission Protocol',
  type: 'Standard',
  status: 'Ready',
  updated: '15/01/2026',
  by: 'System'
}, {
  id: '4',
  name: 'Vitality Coverage Exclusions List',
  type: 'File',
  status: 'Ready',
  updated: '05/03/2026',
  by: 'Billing Admin'
}];
const getTypeBadgeClass = (type: string) => {
  switch (type) {
    case 'Standard':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'File':
      return 'bg-purple-100 text-purple-800 border-purple-200';
    case 'URL':
      return 'bg-amber-100 text-amber-800 border-amber-200';
    default:
      return '';
  }
};
const getStatusBadgeClass = (status: string) => {
  switch (status) {
    case 'Ready':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'Processing':
      return 'bg-amber-100 text-amber-800 border-amber-200';
    default:
      return '';
  }
};
export function KnowledgeBase() {
  const [activeTab, setActiveTab] = useState('coding');
  return <div className="p-8 max-w-[1600px] mx-auto space-y-8" data-id="element-413">
      <div className="flex justify-between items-start" data-id="element-414">
        <div data-id="element-415">
          <h1 className="font-heading text-4xl font-normal text-foreground mb-2" data-id="element-416">
            Knowledge Base
          </h1>
          <p className="text-muted-foreground text-lg" data-id="element-417">
            Manage sources your agent uses for coding and insurance decisions
          </p>
        </div>
        <div className="flex gap-3" data-id="element-418">
          <Button className="bg-primary hover:bg-primary/90 text-primary-foreground gap-2" data-id="element-419">
            <FileText className="w-4 h-4" data-id="element-420" />
            Add File
          </Button>
          <Button className="bg-primary hover:bg-primary/90 text-primary-foreground gap-2" data-id="element-421">
            <LinkIcon className="w-4 h-4" data-id="element-422" />
            Add URL
          </Button>
        </div>
      </div>

      <Tabs defaultValue="coding" value={activeTab} onValueChange={setActiveTab} className="w-full" data-id="element-423">
        <TabsList className="w-full max-w-md grid grid-cols-2 mb-6 bg-secondary" data-id="element-424">
          <TabsTrigger value="coding" className="flex items-center gap-2 data-[state=active]:bg-green-800 data-[state=active]:text-white data-[state=active]:shadow-sm" data-id="element-425">
            <BookOpen className="w-4 h-4" data-id="element-426" />
            Coding Protocols
          </TabsTrigger>
          <TabsTrigger value="insurance" className="flex items-center gap-2 data-[state=active]:bg-green-800 data-[state=active]:text-white data-[state=active]:shadow-sm" data-id="element-427">
            <Shield className="w-4 h-4" data-id="element-428" />
            Insurance Protocols
          </TabsTrigger>
        </TabsList>

        <TabsContent value="coding" data-id="element-429">
          <Card data-id="element-430">
            <Table data-id="element-431">
              <TableHeader data-id="element-432">
                <TableRow data-id="element-433">
                  <TableHead data-id="element-434">Source Name</TableHead>
                  <TableHead data-id="element-435">Type</TableHead>
                  <TableHead data-id="element-436">Status</TableHead>
                  <TableHead data-id="element-437">Last Updated</TableHead>
                  <TableHead data-id="element-438">Updated By</TableHead>
                  <TableHead className="text-right" data-id="element-439">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-id="element-440">
                {codingDocs.map(doc => <TableRow key={doc.id} className="hover:bg-secondary/50" data-id="element-441">
                    <TableCell className="font-medium text-foreground" data-id="element-442">
                      {doc.name}
                    </TableCell>
                    <TableCell data-id="element-443">
                      <Badge variant="outline" className={getTypeBadgeClass(doc.type)} data-id="element-444">
                        {doc.type}
                      </Badge>
                    </TableCell>
                    <TableCell data-id="element-445">
                      <div className="flex items-center gap-2" data-id="element-446">
                        {doc.status === 'Ready' && <CheckCircle2 className="w-4 h-4 text-green-600" data-id="element-447" />}
                        <Badge variant="outline" className={getStatusBadgeClass(doc.status)} data-id="element-448">
                          {doc.status}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground" data-id="element-449">
                      {doc.updated}
                    </TableCell>
                    <TableCell className="text-muted-foreground" data-id="element-450">
                      {doc.by}
                    </TableCell>
                    <TableCell className="text-right" data-id="element-451">
                      <div className="flex justify-end gap-2" data-id="element-452">
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground" data-id="element-453">
                          <Edit className="w-4 h-4" data-id="element-454" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" data-id="element-455">
                          <Trash2 className="w-4 h-4" data-id="element-456" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>)}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="insurance" data-id="element-457">
          <Card data-id="element-458">
            <Table data-id="element-459">
              <TableHeader data-id="element-460">
                <TableRow data-id="element-461">
                  <TableHead data-id="element-462">Source Name</TableHead>
                  <TableHead data-id="element-463">Type</TableHead>
                  <TableHead data-id="element-464">Status</TableHead>
                  <TableHead data-id="element-465">Last Updated</TableHead>
                  <TableHead data-id="element-466">Updated By</TableHead>
                  <TableHead className="text-right" data-id="element-467">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-id="element-468">
                {insuranceDocs.map(doc => <TableRow key={doc.id} className="hover:bg-secondary/50" data-id="element-469">
                    <TableCell className="font-medium text-foreground" data-id="element-470">
                      {doc.name}
                    </TableCell>
                    <TableCell data-id="element-471">
                      <Badge variant="outline" className={getTypeBadgeClass(doc.type)} data-id="element-472">
                        {doc.type}
                      </Badge>
                    </TableCell>
                    <TableCell data-id="element-473">
                      <div className="flex items-center gap-2" data-id="element-474">
                        {doc.status === 'Ready' && <CheckCircle2 className="w-4 h-4 text-green-600" data-id="element-475" />}
                        <Badge variant="outline" className={getStatusBadgeClass(doc.status)} data-id="element-476">
                          {doc.status}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground" data-id="element-477">
                      {doc.updated}
                    </TableCell>
                    <TableCell className="text-muted-foreground" data-id="element-478">
                      {doc.by}
                    </TableCell>
                    <TableCell className="text-right" data-id="element-479">
                      <div className="flex justify-end gap-2" data-id="element-480">
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground" data-id="element-481">
                          <Edit className="w-4 h-4" data-id="element-482" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" data-id="element-483">
                          <Trash2 className="w-4 h-4" data-id="element-484" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>)}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>
    </div>;
}